import { Checkbox, CheckboxProps } from 'antd';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import clx from 'classnames';
import { omit } from 'lodash';
import * as React from 'react';
import { useCallback, useEffect } from 'react';
import { useController } from 'react-hook-form';
import styled from 'styled-components';
import { useField } from '../field/useField';
import { useResetOptionToFalseOnDisable } from '../hooks';
import { useOnFieldDisabled } from '../hooks/use-on-field-disabled/useOnFieldDisabled';
import { useFormItemAccessibility } from '../item/useFormItemAccessibility';

const StyledCheckboxOption = styled(Checkbox)`
  &.read-only {
    pointer-events: none;
  }
`;

export interface ICheckboxIsolatedInputProps
  extends Omit<CheckboxProps, 'name'> {
  /** Nome del gruppo di checkbox. */
  name: string;
  /** Nome della singola opzione */
  optionName: string;
  /** Valore di questa singola checkbox. */
  optionValue: string;
  /** Label della checkbox. */
  // TODO Non supportato attualmente. Il problema risiede nel fatto che è un AdvancedRichText.
  label?: string;
  /**
   * Aggungiamo proprietà readOnly non gestita da antd
   */
  readOnly?: boolean;
}

/**
 * Renderizza un'opzione singola anche se appartiene
 * logicamente ad un Group (multi-selezione).
 */
export function CheckboxOptionInput(props: ICheckboxIsolatedInputProps) {
  const {
    name,
    optionName,
    optionValue,
    value,
    readOnly,
    className,
    ...inputProps
  } = props;

  // Informazioni del gruppo
  const group = useField(name);

  // Informazioni del singolo campo
  const { enabled, visible, unregister } = useField(optionName);
  const { field } = useController({ name: optionName });

  useResetOptionToFalseOnDisable(field, enabled, group.enabled);
  useOnFieldDisabled(field, group.enabled, (methods, name) => {
    methods.setValue(name, group.schema?.[0].getDefault()?.[value], {
      shouldDirty: false,
      shouldValidate: true,
      shouldTouch: false,
    });
  });

  useEffect(() => {
    if (visible) return;
    unregister();
  }, [unregister, visible]);

  const handleChange = useCallback(
    (e: CheckboxChangeEvent) => {
      field.onChange(e.target.checked);

      // Sync per il form, altrimenti non abbiamo le `formReference` per le
      // espressioni calcolate
      // TODO Aggiungere anche all'onload
      group.setValue(group.schema?.[0].cast([]), {
        shouldDirty: false,
        shouldTouch: false,
      });

      // Simuliamo il blur al change
      field.onBlur();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [field, group.setValue, group.schema],
  );

  // TODO Non mi sembra corretto perché qui gestiamo la singola opzione.
  // es. il `label Id` è un altro
  const accessibility = useFormItemAccessibility();

  if (!visible) return null;

  return (
    <StyledCheckboxOption
      {...omit(accessibility.attributes, 'id')}
      {...inputProps}
      disabled={
        group.enabled === false || enabled === false || inputProps.disabled
      }
      ref={field.ref}
      title={props.label}
      checked={!!field.value}
      onChange={readOnly ? undefined : handleChange}
      onClick={readOnly ? undefined : inputProps.onClick}
      className={clx(className, { 'read-only': readOnly })}
    />
  );
}
